import "./AccessGateModal.css";
import { useEffect } from "react";
import { capitalize } from "../utils/capitalize";

// Reusable access-gate modal. Shown when a user reaches gated content their
// plan doesn't cover (no consumed seat at all, or a seat whose tier is too low
// for a specific activity / the Academy). Replaces the old silent redirects so
// the user is told WHAT they need and HOW to get it.
//
// Props:
//   open         — whether the modal is visible
//   title        — short heading
//   message      — body copy explaining what's required
//   requiredTier — optional "parents"|"regular"|"premium" to render a pill
//   actions      — [{ label, onClick, primary? }] rendered as buttons
//   onClose      — optional; when provided the modal is dismissable (×,
//                  backdrop click, Escape). Omit for blocking page-level gates.
export default function AccessGateModal({
  open,
  title,
  message,
  requiredTier,
  actions = [],
  onClose,
}) {
  useEffect(() => {
    if (!open || !onClose) {
      return;
    }
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="gateModalBackdrop"
      onClick={onClose ? onClose : undefined}
    >
      <div
        className="gateModal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        {onClose && (
          <button
            type="button"
            className="gateModalClose"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        )}
        {requiredTier && (
          <span className={`gateTierPill tier-${requiredTier}`}>
            {capitalize(requiredTier)}
          </span>
        )}
        <h2 className="gateModalTitle">{title}</h2>
        <p className="gateModalMessage">{message}</p>
        <div className="gateModalActions">
          {actions.map((action) => (
            <button
              type="button"
              key={action.label}
              className={`gateModalButton${action.primary ? " primary" : ""}`}
              onClick={action.onClick}
            >
              {action.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
